import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Plus, Save, Trash2 } from "lucide-react"
import { useEffect, useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { Area, Campo, type Campos, Grupo, SelectorPais } from "@/componentes/empresa/CamposEmpresa"
import { EncabezadoEmpresa } from "@/componentes/empresa/EncabezadoEmpresa"
import { SubidaLogo } from "@/componentes/empresa/SubidaLogo"
import type { Empresa as Datos } from "@/componentes/empresa/tipos"
import { Boton } from "@/componentes/ui/Boton"
import { Insignia } from "@/componentes/ui/Insignia"
import { tienePermiso } from "@/lib/autorizacion"
import { hoyLocal } from "@/lib/fechas"
import { usarSesion } from "@/lib/sesion"

/**
 * La configuración de la empresa: quién es la agencia y cómo se presenta.
 *
 * ⚠ **Es una sola fila, no un catálogo de empresas.** El 3.0 es el ERP de UNA agencia.
 * `organizacional` es multi-empresa y el 2.0 tiene `empresas_tenants`; arrastrar ese
 * nivel obligaría a que cada organigrama, cada proceso y cada documento llevaran un
 * `empresa_id` que siempre valdría lo mismo, más un selector de una sola opción.
 *
 * ⚠ **La vista previa del encabezado no es adorno: es la pieza de verdad.** Está
 * tomada del 2.0, donde es lo único que deja comprobar cómo va a salir un reporte
 * antes de generarlo. Y aquí pinta **el mismo componente** que usan el manual y los
 * documentos del SGI, así que no es una imitación de la salida — es la salida.
 *
 * ⚠ **Todo lo de esta pantalla sale impreso en algún sitio, y el agrupamiento lo
 * dice.** Identidad → el membrete de todo; legal y parafiscal → contratos y planillas;
 * declaraciones → la portada del manual. Con un formulario plano de veinte campos hay
 * que saberlo de memoria, y entonces nadie sabe qué rompe al cambiar uno.
 *
 * ⚠ **El formulario se llena UNA vez.** Sin eso, cada refresco de la consulta pisaría
 * lo que se está escribiendo — y aquí hay campos largos.
 */

export function Empresa() {
  const clienteQuery = useQueryClient()
  const usuario = usarSesion((e) => e.usuario)
  const puedeEditar = tienePermiso(usuario, "empresa:administrar")

  const [campos, setCampos] = useState<Campos | null>(null)
  const [valorNuevo, setValorNuevo] = useState("")

  const empresa = useQuery({
    queryKey: ["empresa"],
    queryFn: () => api.get<Datos>("/api/v1/empresa"),
  })

  useEffect(() => {
    if (empresa.data && campos === null) setCampos(empresa.data)
  }, [empresa.data, campos])

  // ⚠ Los logos se guardan aparte —son subidas, no parte del PATCH—, así que al
  // cambiarlos llega un objeto nuevo. Se refleja SÓLO esos dos campos, para no pisar
  // lo que se esté escribiendo en el resto del formulario.
  const logoClaro = empresa.data?.tiene_logo
  const logoOscuro = empresa.data?.tiene_logo_oscuro
  useEffect(() => {
    if (logoClaro === undefined || logoOscuro === undefined) return
    setCampos((previos) =>
      previos === null ||
      (previos.tiene_logo === logoClaro && previos.tiene_logo_oscuro === logoOscuro)
        ? previos
        : { ...previos, tiene_logo: logoClaro, tiene_logo_oscuro: logoOscuro },
    )
  }, [logoClaro, logoOscuro])

  const guardar = useMutation({
    mutationFn: () => api.patch<Datos>("/api/v1/empresa", campos ?? {}),
    onSuccess: (nueva) => {
      clienteQuery.invalidateQueries({ queryKey: ["empresa"] })
      // El manual transcribe la identidad: su portada cambia con esto.
      clienteQuery.invalidateQueries({ queryKey: ["manual"] })
      setCampos(nueva)
    },
  })

  if (empresa.isPending || campos === null) {
    return (
      <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
        Cargando la configuración…
      </p>
    )
  }

  if (empresa.isError || !empresa.data) {
    return (
      <p role="alert" className="text-sm" style={{ color: "var(--destructive)" }}>
        No se pudo cargar la configuración de la empresa
      </p>
    )
  }

  const poner = (campo: keyof Datos) => (valor: string) =>
    setCampos((previos) => ({ ...previos, [campo]: valor }))
  const texto = (campo: keyof Datos) => String(campos[campo] ?? "")
  const valores = campos.valores ?? []

  // ⚠ La vista previa pinta lo que se está ESCRIBIENDO, no lo último confirmado. Si
  // enseñara lo guardado, habría que guardar para ver el efecto — y entonces no sirve
  // para decidir si el cambio queda bien.
  const previa = { ...empresa.data, ...campos } as Datos
  const sinGuardar = JSON.stringify(previa) !== JSON.stringify(empresa.data)

  return (
    <div className="max-w-4xl">
      <header className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Configuración de la empresa</h1>
          <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
            El logo y los datos que salen en el encabezado de todo lo que se imprime.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {sinGuardar && <Insignia tono="aviso">Sin guardar</Insignia>}
          {puedeEditar && (
            <Boton
              variante="primario"
              onClick={() => guardar.mutate()}
              disabled={guardar.isPending || !sinGuardar}
            >
              <Save size={16} aria-hidden="true" />
              {guardar.isPending ? "Guardando…" : "Guardar"}
            </Boton>
          )}
        </div>
      </header>

      {guardar.isError && (
        <p role="alert" className="mb-4 text-sm" style={{ color: "var(--destructive)" }}>
          {guardar.error instanceof ErrorApi ? guardar.error.message : "No se pudo guardar"}
        </p>
      )}

      <div className="space-y-6">
        <section className="superficie p-4">
          <h2 className="text-base font-semibold">Vista previa del encabezado</h2>
          <p className="mb-4 text-sm" style={{ color: "var(--fg-muted)" }}>
            Así sale en el manual, en cada documento del SGI y en lo que se exporte a PDF. Es el
            mismo componente, no una imitación.
          </p>
          <div className="superficie p-4">
            <EncabezadoEmpresa empresa={previa} titulo="Documento de ejemplo" fecha={hoyLocal()} />
          </div>
        </section>

        <section className="superficie p-4">
          <h2 className="text-base font-semibold">Logo</h2>
          <p className="mb-4 text-sm" style={{ color: "var(--fg-muted)" }}>
            PNG, JPG o WEBP. Se recomienda fondo transparente. No se admite SVG: lleva scripts
            dentro y servirlo desde nuestro dominio sería un agujero.
          </p>
          <div className="flex flex-wrap gap-4">
            <SubidaLogo modo="claro" empresa={previa} puedeEditar={puedeEditar} />
            <SubidaLogo modo="oscuro" empresa={previa} puedeEditar={puedeEditar} />
          </div>
        </section>

        <Grupo
          titulo="Identidad"
          descripcion="Va en el membrete de todo lo que se imprime y en la firma de correo."
        >
          <Campo
            id="e-nombre"
            etiqueta="Nombre comercial"
            valor={texto("nombre")}
            alCambiar={poner("nombre")}
            soloLectura={!puedeEditar}
          />
          <Campo
            id="e-razon"
            etiqueta="Razón social"
            valor={texto("razon_social")}
            alCambiar={poner("razon_social")}
            ayuda="Manda en el encabezado; el comercial es el respaldo."
            soloLectura={!puedeEditar}
          />
          <Campo
            id="e-rif"
            etiqueta="RIF"
            valor={texto("rif")}
            alCambiar={poner("rif")}
            placeholder="J-12345678-9"
            maxLength={20}
            numerico
            soloLectura={!puedeEditar}
          />
          <Campo
            id="e-etiqueta"
            etiqueta="Etiqueta del sistema"
            valor={texto("etiqueta_sistema")}
            alCambiar={poner("etiqueta_sistema")}
            placeholder="Sistema Aduanal 3.0"
            maxLength={80}
            ayuda="Debajo de la razón social, en pequeño."
            soloLectura={!puedeEditar}
          />
          <Area
            id="e-direccion"
            etiqueta="Dirección"
            valor={texto("direccion")}
            alCambiar={poner("direccion")}
            filas={2}
            soloLectura={!puedeEditar}
          />
          <SelectorPais
            valor={texto("pais_telefono")}
            alCambiar={poner("pais_telefono")}
            soloLectura={!puedeEditar}
          />
          <Campo
            id="e-telefono"
            etiqueta="Teléfono corporativo"
            valor={texto("telefono")}
            alCambiar={poner("telefono")}
            maxLength={40}
            numerico
            ayuda="Lo hereda en su firma quien no tenga uno propio."
            soloLectura={!puedeEditar}
          />
          <Campo
            id="e-email"
            etiqueta="Correo"
            valor={texto("email")}
            alCambiar={poner("email")}
            soloLectura={!puedeEditar}
          />
          <Campo
            id="e-web"
            etiqueta="Sitio web"
            valor={texto("sitio_web")}
            alCambiar={poner("sitio_web")}
            soloLectura={!puedeEditar}
          />
        </Grupo>

        <Grupo
          titulo="Legal y parafiscal"
          descripcion="Lo que la agencia declara de sí misma en un contrato de trabajo o en una planilla."
        >
          <Campo
            id="e-representante"
            etiqueta="Representante legal"
            valor={texto("representante_legal")}
            alCambiar={poner("representante_legal")}
            maxLength={120}
            soloLectura={!puedeEditar}
          />
          <Campo
            id="e-cedula"
            etiqueta="Cédula del representante"
            valor={texto("documento_representante")}
            alCambiar={poner("documento_representante")}
            placeholder="V-12345678"
            maxLength={20}
            numerico
            soloLectura={!puedeEditar}
          />
          <Area
            id="e-registro"
            etiqueta="Registro mercantil"
            valor={texto("registro_mercantil")}
            alCambiar={poner("registro_mercantil")}
            filas={2}
            ayuda="Tomo, número y fecha, tal como se cita en los documentos."
            soloLectura={!puedeEditar}
          />
          <Campo
            id="e-ivss"
            etiqueta="IVSS"
            valor={texto("ivss")}
            alCambiar={poner("ivss")}
            maxLength={50}
            numerico
            soloLectura={!puedeEditar}
          />
          <Campo
            id="e-faov"
            etiqueta="FAOV"
            valor={texto("faov")}
            alCambiar={poner("faov")}
            maxLength={50}
            numerico
            soloLectura={!puedeEditar}
          />
          <Campo
            id="e-inces"
            etiqueta="INCES"
            valor={texto("inces")}
            alCambiar={poner("inces")}
            maxLength={50}
            numerico
            soloLectura={!puedeEditar}
          />
          <Campo
            id="e-nil"
            etiqueta="NIL"
            valor={texto("nil")}
            alCambiar={poner("nil")}
            maxLength={50}
            numerico
            soloLectura={!puedeEditar}
          />
        </Grupo>

        <Grupo
          titulo="Declaraciones"
          descripcion="Encabezan el manual y son de donde cuelgan los pilares del despliegue estratégico."
        >
          <Area
            id="e-vision"
            etiqueta="Visión"
            valor={texto("vision")}
            alCambiar={poner("vision")}
            soloLectura={!puedeEditar}
          />
          <Area
            id="e-mision"
            etiqueta="Misión"
            valor={texto("mision")}
            alCambiar={poner("mision")}
            soloLectura={!puedeEditar}
          />
          <div className="sm:col-span-2">
            <p className="mb-1 text-xs font-medium uppercase">Valores</p>
            <ul className="mb-2 space-y-1">
              {valores.map((v, i) => (
                <li key={v} className="flex items-center gap-2 text-sm">
                  <span className="flex-1">{v}</span>
                  {puedeEditar && (
                    <button
                      type="button"
                      onClick={() =>
                        setCampos({ ...campos, valores: valores.filter((_, j) => j !== i) })
                      }
                      aria-label={`Quitar ${v}`}
                      style={{ color: "var(--destructive)" }}
                    >
                      <Trash2 size={14} aria-hidden="true" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
            {puedeEditar && (
              <div className="flex gap-2">
                <input
                  value={valorNuevo}
                  onChange={(e) => setValorNuevo(e.target.value)}
                  placeholder="Añadir un valor"
                  aria-label="Añadir un valor"
                  className="h-9 min-w-0 flex-1 rounded-[var(--radius-control)] border px-2 text-sm"
                  style={{
                    borderColor: "var(--border)",
                    backgroundColor: "var(--surface)",
                    color: "var(--fg)",
                  }}
                />
                <Boton
                  variante="secundario"
                  onClick={() => {
                    if (!valorNuevo.trim()) return
                    setCampos({ ...campos, valores: [...valores, valorNuevo.trim()] })
                    setValorNuevo("")
                  }}
                >
                  <Plus size={15} aria-hidden="true" />
                  Añadir
                </Boton>
              </div>
            )}
            <p className="mt-1 text-xs" style={{ color: "var(--fg-muted)" }}>
              En orden: es el que sale impreso en el manual.
            </p>
          </div>
        </Grupo>

        <Grupo
          titulo="Documentos controlados"
          descripcion="El prefijo con el que se acuñan los códigos de la biblioteca del SGI."
        >
          <Campo
            id="e-prefijo"
            etiqueta="Prefijo"
            valor={texto("codigo_prefijo")}
            alCambiar={poner("codigo_prefijo")}
            maxLength={40}
            placeholder="SETIECA-SGI"
            ayuda="Los documentos ya emitidos conservan el código que tenían: se cita en otros."
            soloLectura={!puedeEditar}
          />
        </Grupo>
      </div>
    </div>
  )
}
